import {
  dbui_v1_ObservableList,
  DbuiV1GetObservableKindData,
  dbuiV1GetObservableKindInfiniteOptions,
  dbuiV1GetObservableKindInfiniteQueryKey,
} from '@antimatterhq/asl-api-ts-v1';
import {
  InfiniteData,
  useInfiniteQuery,
} from '@antimatterhq/ui/hooks/queries/useInfiniteQuery';
import { useQueryClient } from '@antimatterhq/ui/hooks/queries/useQueryClient';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWarehouseLocation } from '@/contexts/WarehouseLocationContext';
import { defaultOptions } from '@/utils/query';

type QueryOptions = Omit<
  NonNullable<DbuiV1GetObservableKindData['query']>,
  'warehouse'
> & { kind?: string };

export const useGetObservablesKind = ({
  limit = 10,
  kind = '',
  cursor = '',
}: QueryOptions) => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';

  return useInfiniteQuery({
    ...dbuiV1GetObservableKindInfiniteOptions({
      ...defaultOptions(),
      path: { workspace, kind },
      query: { warehouse, cursor, limit },
    }),
    enabled: !!workspace || !!kind,
    getNextPageParam: (page) => page.cursor,
  });
};

export const useOptimisticUpdateObservablesKind = ({
  limit = 10,
  kind = '',
  cursor = '',
}: QueryOptions) => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';
  const queryClient = useQueryClient();

  return (observables?: InfiniteData<dbui_v1_ObservableList, unknown>) => {
    if (!observables) return;
    queryClient.setQueryData(
      dbuiV1GetObservableKindInfiniteQueryKey({
        ...defaultOptions(),
        path: { workspace, kind },
        query: { warehouse, cursor, limit },
      }),
      observables,
    );
  };
};

export const useClearCacheObservablesKind = () => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';
  const queryClient = useQueryClient();
  return ({ kind }: { kind: string }) => {
    queryClient.removeQueries({
      queryKey: dbuiV1GetObservableKindInfiniteQueryKey({
        ...defaultOptions(),
        path: { workspace, kind },
        query: { warehouse },
      }),
      exact: false,
    });
  };
};
